/**
 * Notification chime (UIUX §5.5). One short synthesized tone — no
 * audio asset, no dependency. Never for routine messages: callers
 * gate on the event key via `chimeEnabledFor`.
 *
 * Defaults per the B2 adjudication: ON for escalations (the ai.*
 * handoff family — missed-call events don't exist in the catalog
 * yet), OPT-IN for SLA breaches (a Monday-open sweep can breach 40
 * leads at once). Persisted per-user in localStorage exactly like
 * `lumika.theme`.
 */

const ESCALATION_SOUND_KEY = "lumika.sound.escalations";
const SLA_SOUND_KEY = "lumika.sound.sla";

/** Escalation-severity event keys (mirror of the seeded catalog). */
const ESCALATION_EVENT_KEYS = new Set([
  "ai.needs_help",
  "ai.flagged_emergency",
  "ai.no_response_timeout",
  "ai.customer_asked_for_manager",
]);

function readFlag(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw === "1";
  } catch {
    return fallback;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // localStorage may be unavailable; the toggle still works for the session.
  }
}

export function escalationSoundEnabled(): boolean {
  return readFlag(ESCALATION_SOUND_KEY, true); // default ON
}
export function setEscalationSoundEnabled(value: boolean): void {
  writeFlag(ESCALATION_SOUND_KEY, value);
}
export function slaSoundEnabled(): boolean {
  return readFlag(SLA_SOUND_KEY, false); // opt-in
}
export function setSlaSoundEnabled(value: boolean): void {
  writeFlag(SLA_SOUND_KEY, value);
}

/** Should this notification event chime, per the user's toggles? */
export function chimeEnabledFor(eventKey: string): boolean {
  if (ESCALATION_EVENT_KEYS.has(eventKey)) return escalationSoundEnabled();
  if (eventKey === "sla.breached") return slaSoundEnabled();
  return false; // routine events never chime
}

let audioContext: AudioContext | null = null;

/** Two quick descending sine notes — short, unmistakable, not a klaxon. */
export function playChime(): void {
  try {
    audioContext = audioContext ?? new AudioContext();
    const ctx = audioContext;
    const now = ctx.currentTime;
    for (const [offset, freq] of [
      [0, 880],
      [0.12, 660],
    ] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + offset);
      gain.gain.exponentialRampToValueAtTime(0.12, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + offset);
      osc.stop(now + offset + 0.3);
    }
  } catch {
    // Autoplay policy or missing AudioContext — silently skip; the
    // bell and title badge still carry the signal.
  }
}
